<template>
  <div class="space-y-6">
    <!-- En-tête -->

    <!-- Graphiques en ligne (l'un en dessous de l'autre) -->
    <div class="space-y-6">
      <!-- Graphique Tickets par Responsable - Barres horizontales -->
      <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
        <div class="flex justify-between items-center mb-6">
          <h3 class="text-lg font-semibold text-blue-900 dark:text-blue-300">Tickets Reçu par Responsable</h3>
        </div>
        <div class="h-80">
          <canvas ref="responsableChart"></canvas>
        </div>
        <div class="mt-4 text-sm text-gray-500 dark:text-gray-400 text-center">
          Répartition des tickets selon les responsables assignés
        </div>
      </div>

      <!-- Graphique Statuts par Responsable -->
      <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
        <div class="flex justify-between items-center mb-6">
          <h3 class="text-lg font-semibold text-blue-900 dark:text-blue-300">Statuts par Responsable</h3>
        </div>
        <div class="h-96"> <!-- Augmenté la hauteur pour plus d'espace -->
          <canvas ref="responsableStatusChart"></canvas>
        </div>
        <div class="mt-4 text-sm text-gray-500 dark:text-gray-400 text-center">
          Répartition des statuts pour chaque responsable
        </div>
      </div>

      <!-- Section Performance du support client -->
      <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
        <div class="flex justify-between items-center mb-6">
          <h3 class="text-lg font-semibold text-blue-900 dark:text-blue-300">Performance du support client</h3>
          
          <!-- Sélecteur de responsable -->
          <div class="flex items-center gap-4">
            <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Responsable :</label>
            <select 
              v-model="selectedResponsable" 
              @change="loadPerformanceData"
              class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tous les responsables</option>
              <option 
                v-for="responsable in responsablesList" 
                :key="responsable.id" 
                :value="responsable.id"
              >
                {{ responsable.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Métriques de performance -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <!-- Temps de réponse moyen -->
          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-6 shadow-sm">
            <div class="flex items-center">
              <div class="p-3 rounded-full bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 mr-4">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Temps réponse moyen</p>
                <p class="text-2xl font-bold text-blue-900 dark:text-blue-300">{{ performanceStats.tempsReponseMoyen }}h</p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 font-bold">
                  {{ totalTickets }} T
                </p>
              </div>
            </div>
          </div>
          
          <!-- Temps de fermeture moyen -->
          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-6 shadow-sm">
            <div class="flex items-center">
              <div class="p-3 rounded-full bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 mr-4">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Temps fermeture moyen</p>
                <p class="text-2xl font-bold text-blue-900 dark:text-blue-300">{{ performanceStats.tempsFermetureMoyen }}h</p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt- font-bold">
                  {{ totalTickets }} T
                </p>
              </div>
            </div>
          </div>
          
          <!-- Taux de réponse -->
          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-6 shadow-sm">
            <div class="flex items-center">
              <div class="p-3 rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 mr-4">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                </svg>
              </div>
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Taux de réponse</p>
                <p class="text-2xl font-bold text-blue-900 dark:text-blue-300">{{ performanceStats.tauxReponse }}%</p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 font-bold">
                  {{ totalTickets }} T
                </p>
              </div>
            </div>
          </div>

          <!-- Respect des délais -->
          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-6 shadow-sm">
            <div class="flex items-center">
              <div class="p-3 rounded-full bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 mr-4">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Respect des délais</p>
                <p class="text-2xl font-bold text-blue-900 dark:text-blue-300">{{ delaisStats.pourcentageDansDelais }}%</p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 font-bold">
                  {{ totalTickets }} T
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Graphiques des performances -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <!-- Graphique Évolution des Temps de Traitement -->
          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-6 shadow-sm">
            <div class="flex justify-between items-center mb-4">
              <h4 class="text-md font-semibold text-blue-900 dark:text-blue-300">Évolution des Temps de Traitement</h4>
            </div>
            <div class="h-64">
              <canvas ref="traitementTimeChart"></canvas>
            </div>
          </div>
          
          <!-- Graphique Taux de Réponse Mensuel -->
          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-6 shadow-sm">
            <div class="flex justify-between items-center mb-4">
              <h4 class="text-md font-semibold text-blue-900 dark:text-blue-300">Taux de Réponse Mensuel</h4>
            </div>
            <div class="h-64">
              <canvas ref="tauxReponseChart"></canvas>
            </div>
          </div>
        </div>

        <!-- NOUVELLE SECTION : Analyse des délais de réponse -->
        <div class="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-6 shadow-sm">
          <div class="flex justify-between items-center mb-6">
            <h4 class="text-lg font-semibold text-blue-900 dark:text-blue-300">Analyse des Délais de Réponse</h4>
          </div>

          <!-- Métriques des délais -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <!-- Délai moyen réel -->
            <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4">
              <div class="text-center">
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Délai de réponse moyen</p>
                <p class="text-2xl font-bold text-blue-900 dark:text-blue-300">{{ delaisStats.delaiMoyenReel }}h</p>
                <p class="text-xs text-gray-500 dark:text-gray-400 font-bold">
                  {{ totalTickets }} T
                </p>
              </div>
            </div>

            <!-- Délai moyen attendu -->
            <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4">
              <div class="text-center">
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Délai moyen attendu</p>
                <p class="text-2xl font-bold text-green-900 dark:text-green-300">{{ delaisStats.delaiMoyenAttendu }}h</p>
                <p class="text-xs text-gray-500 dark:text-gray-400 font-bold">
                  {{ totalTickets }} T
                </p>
              </div>
            </div>

            <!-- Écart moyen -->
            <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4">
              <div class="text-center">
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Écart moyen</p>
                <p class="text-2xl font-bold" :class="delaisStats.ecartMoyen >= 0 ? 'text-red-900 dark:text-red-300' : 'text-green-900 dark:text-green-300'">
                  {{ delaisStats.ecartMoyen >= 0 ? '+' : '' }}{{ delaisStats.ecartMoyen }}h
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400 font-bold" :class="delaisStats.ecartMoyen >= 0 ? 'text-red-500' : 'text-green-500'">
                  {{ totalTickets }} T
                </p>
              </div>
            </div>
          </div>

          <!-- Graphique Comparaison Délais Réels vs Attendus -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Graphique Comparaison Délais -->
            <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-6">
              <div class="flex justify-between items-center mb-4">
                <h5 class="text-md font-semibold text-blue-900 dark:text-blue-300">Délais Réels vs Attendus</h5>
              </div>
              <div class="h-64">
                <canvas ref="delaisComparisonChart"></canvas>
              </div>
            </div>

            <!-- Graphique Répartition Respect Délais -->
            <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-6">
              <div class="flex justify-between items-center mb-4">
                <h5 class="text-md font-semibold text-blue-900 dark:text-blue-300">Respect des Délais</h5>
              </div>
              <div class="h-64">
                <canvas ref="delaisRespectChart"></canvas>
              </div>
            </div>
          </div>

          <!-- Tableau détaillé des délais par motif -->
          <div class="mt-8">
            <h5 class="text-md font-semibold text-blue-900 dark:text-blue-300 mb-4">Détail par Motif</h5>
            <div class="overflow-x-auto">
              <table class="min-w-full bg-white dark:bg-gray-800 rounded-lg">
                <thead>
                  <tr class="bg-gray-100 dark:bg-gray-700">
                    <th class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Motif</th>
                    <th class="px-4 py-2 text-center text-sm font-medium text-gray-700 dark:text-gray-300">Délai attendu</th>
                    <th class="px-4 py-2 text-center text-sm font-medium text-gray-700 dark:text-gray-300">Délai réel moyen</th>
                    <th class="px-4 py-2 text-center text-sm font-medium text-gray-700 dark:text-gray-300">Écart</th>
                    <th class="px-4 py-2 text-center text-sm font-medium text-gray-700 dark:text-gray-300">Tickets</th>
                    <th class="px-4 py-2 text-center text-sm font-medium text-gray-700 dark:text-gray-300">Dans les délais</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in delaisStats.detailsMotifs" :key="index" 
                      class="border-b border-gray-200 dark:border-gray-600">
                    <td class="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{{ item.motif }}</td>
                    <td class="px-4 py-2 text-sm text-center text-green-600 dark:text-green-400">
                      {{ item.delaiAttendu }}h<br>
                      <span class="text-xs text-gray-500">/{{ totalTickets }} T</span>
                    </td>
                    <td class="px-4 py-2 text-sm text-center text-blue-600 dark:text-blue-400">
                      {{ item.delaiReelMoyen }}h<br>
                      <span class="text-xs text-gray-500">/{{ totalTickets }} T</span>
                    </td>
                    <td class="px-4 py-2 text-sm text-center" 
                        :class="item.ecart >= 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'">
                      {{ item.ecart >= 0 ? '+' : '' }}{{ item.ecart }}h<br>
                      <span class="text-xs text-gray-500">/{{ totalTickets }} T</span>
                    </td>
                    <td class="px-4 py-2 text-sm text-center text-gray-600 dark:text-gray-400">{{ item.nombreTickets }}</td>
                    <td class="px-4 py-2 text-sm text-center">
                      <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                            :class="item.pourcentageDansDelais >= 80 ? 'bg-green-100 text-green-800' : 
                                    item.pourcentageDansDelais >= 60 ? 'bg-yellow-100 text-yellow-800' : 
                                    'bg-red-100 text-red-800'">
                        {{ item.pourcentageDansDelais }}%
                      </span>
                      <br>
                      <span class="text-xs text-gray-500">
                        /{{ totalTickets }} T
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

export default {
  name: 'StatisticsContent',
  props: {
    userRoles: {
      type: Array,
      default: () => []
    },
    userPermissions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      chartInstances: {},
      userRole: 'Utilisateur',
      selectedResponsable: 'all',
      responsablesList: [],
      totalTickets: 0,
      performanceStats: {
        tempsReponseMoyen: 0,
        tempsFermetureMoyen: 0,
        tauxReponse: 0,
        respectDelais: 0
      },
      delaisStats: {
        delaiMoyenReel: 0,
        delaiMoyenAttendu: 0,
        ecartMoyen: 0,
        detailsMotifs: []
      },
      chartsInitialized: false
    };
  },
  computed: {
    isAdmin() {
      const roles = this.userRoles || [];
      return roles.includes('Administrateur') ||
        roles.includes('administrateur') ||
        roles.includes('admin');
    },
    isResponsable() {
      const roles = this.userRoles || [];
      return roles.includes('Responsable') ||
        roles.includes('responsable');
    }
  },
  mounted() {
    this.determineUserRole();
    this.loadCharts();
    this.loadResponsablesList();
  },
  
  beforeUnmount() {
    this.destroyAllCharts();
  },
  
  activated() {
    if (!this.chartsInitialized) {
      this.$nextTick(() => {
        this.loadCharts();
      });
    }
  },
  
  deactivated() {
    this.destroyAllCharts();
  },

  methods: {
    determineUserRole() {
      if (this.isAdmin) {
        this.userRole = 'Administrateur';
      } else if (this.isResponsable) {
        this.userRole = 'Responsable';
      } else {
        this.userRole = 'Utilisateur';
      }
    },

    destroyAllCharts() {
      Object.keys(this.chartInstances).forEach(key => {
        if (this.chartInstances[key]) {
          try {
            this.chartInstances[key].destroy();
          } catch (error) {
            console.warn(`Erreur destruction graphique ${key}:`, error);
          }
          this.chartInstances[key] = null;
        }
      });
      this.chartsInitialized = false;
    },

    ensureCanvasReady() {
      return new Promise((resolve) => {
        const checkRefs = () => {
          const refs = ['responsableChart', 'responsableStatusChart', 'traitementTimeChart', 
                       'tauxReponseChart', 'delaisComparisonChart', 'delaisRespectChart'];
          const allReady = refs.every(ref => this.$refs[ref]);
          
          if (allReady) {
            resolve(true);
          } else {
            setTimeout(checkRefs, 50);
          }
        };
        checkRefs();
      });
    },

    async loadResponsablesList() {
      try {
        const response = await fetch('/api/responsables');
        const data = await response.json();
        
        if (data.success) {
          this.responsablesList = data.responsables;
        } else {
          console.error('Erreur chargement responsables:', data.message);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des responsables:', error);
      }
    },

    async loadCharts() {
      this.destroyAllCharts();
      
      this.loading = true;
      try {
        await this.ensureCanvasReady();
        
        const response = await fetch('/api/dashboard/statistics');
        const data = await response.json();
        
        if (data.success) {
          await this.renderCharts(data.data);
          this.loadPerformanceData();
          this.chartsInitialized = true;
        } else {
          console.error('Erreur API:', data.message);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des graphiques:', error);
        this.showError('Erreur lors du chargement des statistiques');
      } finally {
        this.loading = false;
      }
    },

    async loadPerformanceData() {
      try {
        const url = this.selectedResponsable === 'all' 
          ? '/api/dashboard/performance'
          : `/api/dashboard/performance/${this.selectedResponsable}`;
        
        const response = await fetch(url);
        const data = await response.json();
        
        if (data.success) {
          this.performanceStats = data.performance;
          this.delaisStats = data.delaisStats || this.delaisStats;
          
          // Calculer le total des tickets
          this.calculateTotalTickets();
          
          if (data.charts) {
            this.renderPerformanceCharts(data.charts);
          }
        } else {
          console.error('Erreur chargement performance:', data.message);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données de performance:', error);
      }
    },

    calculateTotalTickets() {
      // Calculer le total des tickets basé sur les données disponibles
      if (this.delaisStats.totalTicketsAnalyses && this.delaisStats.totalTicketsAnalyses > 0) {
        this.totalTickets = this.delaisStats.totalTicketsAnalyses;
      } else if (this.performanceStats.totalTicketsAnalyses && this.performanceStats.totalTicketsAnalyses > 0) {
        this.totalTickets = this.performanceStats.totalTicketsAnalyses;
      } else {
        // Si aucune donnée n'est disponible, utiliser une valeur par défaut ou calculer à partir des détails
        this.totalTickets = this.delaisStats.detailsMotifs.reduce((total, motif) => {
          return total + (motif.nombreTickets || 0);
        }, 0) || 1; // Éviter la division par zéro
      }
    },

    async renderCharts(chartData) {
      await this.ensureCanvasReady();
      
      if (chartData.responsable) {
        this.renderHorizontalBarChart('responsableChart', chartData.responsable);
      }
      
      if (chartData.responsable_status) {
        this.renderResponsableStatusChart('responsableStatusChart', chartData.responsable_status);
      }
      
      if (chartData.traitement_times) {
        this.renderTraitementTimeChart('traitementTimeChart', chartData.traitement_times);
      }
      
      if (chartData.taux_reponse) {
        this.renderTauxReponseChart('tauxReponseChart', chartData.taux_reponse);
      }
      
      if (chartData.delais_comparison) {
        this.renderDelaisComparisonChart('delaisComparisonChart', chartData.delais_comparison);
      }
      
      if (chartData.delais_respect) {
        this.renderDelaisRespectChart('delaisRespectChart', chartData.delais_respect);
      }
    },

    renderPerformanceCharts(chartData) {
      if (chartData.traitement_times) {
        this.renderTraitementTimeChart('traitementTimeChart', chartData.traitement_times);
      }
      
      if (chartData.taux_reponse) {
        this.renderTauxReponseChart('tauxReponseChart', chartData.taux_reponse);
      }
      
      if (chartData.delais_comparison) {
        this.renderDelaisComparisonChart('delaisComparisonChart', chartData.delais_comparison);
      }
      
      if (chartData.delais_respect) {
        this.renderDelaisRespectChart('delaisRespectChart', chartData.delais_respect);
      }
    },

    // Les autres méthodes restent inchangées...
    renderHorizontalBarChart(ref, data) {
      if (!this.$refs[ref]) {
        console.warn(`Canvas ${ref} non disponible`);
        return;
      }
      
      const ctx = this.$refs[ref].getContext('2d');
      
      if (this.chartInstances[ref]) {
        try {
          this.chartInstances[ref].destroy();
        } catch (error) {
          console.warn(`Erreur destruction ${ref}:`, error);
        }
      }

      try {
        this.chartInstances[ref] = new Chart(ctx, {
          type: 'bar',
          data: data,
          options: {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    return `${context.parsed.x} tickets`;
                  }
                }
              }
            },
            scales: {
              x: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Nombre de tickets'
                },
                ticks: {
                  precision: 0
                }
              },
              y: {
                title: {
                  display: true,
                  text: 'Responsables'
                }
              }
            }
          }
        });
      } catch (error) {
        console.error(`Erreur création graphique ${ref}:`, error);
      }
    },

    renderResponsableStatusChart(ref, data) {
      if (!this.$refs[ref]) {
        console.warn(`Canvas ${ref} non disponible`);
        return;
      }
      
      const ctx = this.$refs[ref].getContext('2d');
      
      if (this.chartInstances[ref]) {
        try {
          this.chartInstances[ref].destroy();
        } catch (error) {
          console.warn(`Erreur destruction ${ref}:`, error);
        }
      }

      try {
        this.chartInstances[ref] = new Chart(ctx, {
          type: 'bar',
          data: data,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'top',
                labels: {
                  boxWidth: 12,
                  font: {
                    size: 11
                  },
                  padding: 10
                }
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    return `${context.dataset.label}: ${context.parsed.y} tickets`;
                  }
                }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Nombre de tickets'
                },
                ticks: {
                  precision: 0,
                  font: {
                    size: 11
                  }
                }
              },
              x: {
                title: {
                  display: true,
                  text: 'Responsables'
                },
                ticks: {
                  font: {
                    size: 10
                  },
                  maxRotation: 45,
                  minRotation: 0,
                  callback: function(value) {
                    const label = this.getLabelForValue(value);
                    if (label.length > 15) {
                      return label.substring(0, 15) + '...';
                    }
                    return label;
                  }
                }
              }
            },
            layout: {
              padding: {
                left: 10,
                right: 10,
                top: 10,
                bottom: 10
              }
            }
          }
        });
      } catch (error) {
        console.error(`Erreur création graphique ${ref}:`, error);
      }
    },

    renderTraitementTimeChart(ref, data) {
      if (!this.$refs[ref]) {
        console.warn(`Canvas ${ref} non disponible`);
        return;
      }
      
      const ctx = this.$refs[ref].getContext('2d');
      
      if (this.chartInstances[ref]) {
        try {
          this.chartInstances[ref].destroy();
        } catch (error) {
          console.warn(`Erreur destruction ${ref}:`, error);
        }
      }

      if (!data || !data.labels || data.labels.length === 0) {
        this.renderEmptyChart(ref, 'Aucune donnée de temps de traitement disponible');
        return;
      }

      try {
        this.chartInstances[ref] = new Chart(ctx, {
          type: 'line',
          data: data,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'top',
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    return `${context.dataset.label}: ${context.parsed.y}h`;
                  }
                }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Temps (heures)'
                }
              },
              x: {
                title: {
                  display: true,
                  text: 'Mois'
                }
              }
            }
          }
        });
      } catch (error) {
        console.error(`Erreur création graphique ${ref}:`, error);
      }
    },

    renderTauxReponseChart(ref, data) {
      if (!this.$refs[ref]) {
        console.warn(`Canvas ${ref} non disponible`);
        return;
      }
      
      const ctx = this.$refs[ref].getContext('2d');
      
      if (this.chartInstances[ref]) {
        try {
          this.chartInstances[ref].destroy();
        } catch (error) {
          console.warn(`Erreur destruction ${ref}:`, error);
        }
      }

      if (!data || !data.labels || data.labels.length === 0) {
        this.renderEmptyChart(ref, 'Aucune donnée de taux de réponse disponible');
        return;
      }

      try {
        this.chartInstances[ref] = new Chart(ctx, {
          type: 'bar',
          data: data,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'top',
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    return `${context.dataset.label}: ${context.parsed.y}%`;
                  }
                }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                max: 100,
                title: {
                  display: true,
                  text: 'Taux de réponse (%)'
                }
              },
              x: {
                title: {
                  display: true,
                  text: 'Mois'
                }
              }
            }
          }
        });
      } catch (error) {
        console.error(`Erreur création graphique ${ref}:`, error);
      }
    },

    renderDelaisComparisonChart(ref, data) {
      if (!this.$refs[ref]) {
        console.warn(`Canvas ${ref} non disponible`);
        return;
      }
      
      const ctx = this.$refs[ref].getContext('2d');
      
      if (this.chartInstances[ref]) {
        try {
          this.chartInstances[ref].destroy();
        } catch (error) {
          console.warn(`Erreur destruction ${ref}:`, error);
        }
      }

      if (!data || !data.labels || data.labels.length === 0) {
        this.renderEmptyChart(ref, 'Aucune donnée de comparaison des délais disponible');
        return;
      }

      try {
        this.chartInstances[ref] = new Chart(ctx, {
          type: 'bar',
          data: data,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'top',
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    return `${context.dataset.label}: ${context.parsed.y}h`;
                  }
                }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Temps (heures)'
                }
              },
              x: {
                title: {
                  display: true,
                  text: 'Motifs'
                }
              }
            }
          }
        });
      } catch (error) {
        console.error(`Erreur création graphique ${ref}:`, error);
      }
    },

    renderDelaisRespectChart(ref, data) {
      if (!this.$refs[ref]) {
        console.warn(`Canvas ${ref} non disponible`);
        return;
      }
      
      const ctx = this.$refs[ref].getContext('2d');
      
      if (this.chartInstances[ref]) {
        try {
          this.chartInstances[ref].destroy();
        } catch (error) {
          console.warn(`Erreur destruction ${ref}:`, error);
        }
      }

      if (!data || !data.labels || data.labels.length === 0) {
        this.renderEmptyChart(ref, 'Aucune donnée de respect des délais disponible');
        return;
      }

      try {
        this.chartInstances[ref] = new Chart(ctx, {
          type: 'doughnut',
          data: data,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const label = context.label || '';
                    const value = context.parsed;
                    const total = context.dataset.data.reduce((a, b) => a + b, 0);
                    const percentage = Math.round((value / total) * 100);
                    return `${label}: ${value} tickets (${percentage}%)`;
                  }
                }
              }
            }
          }
        });
      } catch (error) {
        console.error(`Erreur création graphique ${ref}:`, error);
      }
    },

    renderEmptyChart(ref, message) {
      if (!this.$refs[ref]) return;
      
      const ctx = this.$refs[ref].getContext('2d');
      ctx.clearRect(0, 0, this.$refs[ref].width, this.$refs[ref].height);
      ctx.fillStyle = '#6B7280';
      ctx.font = '14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(message, this.$refs[ref].width / 2, this.$refs[ref].height / 2);
    },

    showError(message) {
      alert('Erreur: ' + message);
    }
  }
};
</script>